import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {Button, Dropdown, List, Message, MessageHeader, Modal, Table} from "semantic-ui-react";
import {Action as NotifyAction} from "../../../NotificationSystem";
import {makeGetCurrentUser} from "../../../User/selectors/currentUser";
import {makeIioFilteredUserListSelector} from "../../../User/selectors/iioUserInfo";
import * as Action from "../../actions";
import {toggleCodyConsole, togglePlayshotModal} from "../../actions/commands";
import {useElementsTree} from "../../hooks/useElementsTree";
import {useGraph} from "../../hooks/useGraph";
import {BoardId} from "../../model/Board";
import {Node} from "../../model/Graph";
import {CodyPlayConfig, Playshot} from "../../model/Playshot";
import {PlayshotId, PlayshotSummary} from "../../model/PlayshotSummary";
import {makeBoardByIdSelector} from "../../selectors/board";
import {makePlayshotSummaryListSelector} from "../../selectors/PlayshotSummary";
import {CODY_PLAY_SERVER_NAME, CodyResponse, CodyResponseType, useIIO} from "../../service/Cody";
import {deletePlayshot, loadPlayshot} from "../../service/playshot-storage";
import {comparePlayshots} from "../../service/playshot/compare-playshots";
import {PlayshotChanged} from "../../service/playshot/diff/PlayshotChanged";
import PlayshotChangedDiff from "./PlayshotChangedDiff";
import PlayshotTableRow from "./PlayshotTableRow";

interface PlayshotModalRouteMatchParams {
  uid: BoardId;
}

interface OwnProps {

}

export interface PlayshotComparison {
  left: Playshot;
  right: Playshot;
  applied?: boolean;
  error?: boolean;
}

export const CODY_APPLY_PAUSE = 1000;

type PlayshotModalProps = OwnProps & RouteComponentProps<PlayshotModalRouteMatchParams> & WithNamespaces;

const PlayshotModal = (props: PlayshotModalProps) => {
  const dispatch = useDispatch();
  const [graph] = useGraph();
  const elementsTree = useElementsTree(props.match.params.uid);
  const getBoardByIdSelector = useMemo(() => makeBoardByIdSelector(props.match.params.uid), []);
  const board = useSelector(getBoardByIdSelector);
  const visible = board && board.shouldShowPlayshotModal;
  const playshots = useSelector(makePlayshotSummaryListSelector(props.match.params.uid));
  const [loadingPlayshotId, setLoadingPlayshotId] = useState('');
  const [playshotComparision, setPlayshotComparision] = useState<PlayshotComparison | undefined>(undefined);
  const [syncError, setSyncError] = useState<string|{title: string, msg: string}|undefined>(undefined);
  const [playshotDeletedCount, setPlayshotDeletedCount] = useState(0);

  const userIds = playshots.map(playshot => playshot.savedBy);
  const users = useSelector(makeIioFilteredUserListSelector(userIds));
  const iio = useIIO();
  const currentUser = useSelector(makeGetCurrentUser());

  useEffect(() => {
    if(visible) {
      dispatch(Action.Query.fetchPlayshotSummaryList(props.match.params.uid));
    }
  }, [visible, playshotDeletedCount]);

  const handleClose = () => {
    setLoadingPlayshotId('');
    setPlayshotComparision(undefined);
    setSyncError(undefined);
    dispatch(togglePlayshotModal(props.match.params.uid, false));
  }

  const initCodyPlay = (playshot: Playshot): void => {
    if(!board) {
      dispatch(NotifyAction.Command.error('Failed to load Board', 'Please contact the prooph board team!'))
      return;
    }

    if(!iio.isConnected(CODY_PLAY_SERVER_NAME)) {
      iio.connect.Cody('play', currentUser.name, board.uid, CODY_PLAY_SERVER_NAME);
    }

    iio.runCommand(board.uid, board.name, currentUser.uid, 'InitPlayshot', playshot, CODY_PLAY_SERVER_NAME);

    window.setTimeout(handleClose, 500);
  }

  const handleInitPlayshot = (playshotId: PlayshotId) => {
    setLoadingPlayshotId(playshotId);

    loadPlayshot(board.uid, playshotId).then(playshot => {
      initCodyPlay(playshot);
    }).catch((e: Error) => {
      dispatch(NotifyAction.Command.error('Failed to load playshot', e.message));
    })
  }

  const handleDeletePlayshot = (playshotId: PlayshotId) => {
    deletePlayshot(playshotId, board.uid).then(() => {
        setPlayshotDeletedCount((prev) => prev + 1)
    })
  }

  const handleFromScratch = () => {
    initCodyPlay(new Playshot({
      name: 'From Scratch Playshot',
      playConfig: {} as unknown as CodyPlayConfig,
      playData: {streams: {}, documents: {}},
      savedBy: currentUser.uid,
      boardId: board.uid,
    }))
  }



  if(playshotComparision) {
    const changeSet = comparePlayshots(playshotComparision!.left, playshotComparision!.right, graph, elementsTree);

    const numOfChanges = changeSet.length;
    let error = false;
    let lastResponse: CodyResponse | undefined;
    const addedNodes: {[nodeId: string]: Node} = {};
    const syncContext = elementsTree.getFilteredElementsByTag('#PlayshotSync').first(undefined);

    if(!syncContext && !syncError ) {
      setSyncError('Missing a #PlayshotSync context on the board.')
    }

    if(syncContext && syncContext.children().count() > 1 && !syncError) {
      setSyncError({
        title: '#PlayshotSync context is not empty!',
        msg: 'You have to move already generated elements out of the sync context, before Cody can run the next session.'
      })
    }

    const syncChange = async (change: PlayshotChanged, currentIteration: number) => {
      if(!syncContext || syncError) {
        return;
      }

      for await(const response of await change.apply({
        syncContext,
        graph,
        playshot: playshotComparision!.right,
        elementsTree,
        addedNodes
      })) {
        if(response.type !== CodyResponseType.Empty) {
          iio.print(response, 'Cody');
        }

        lastResponse = response;

        if(response.type === CodyResponseType.Error) {
          error = true;
        }
      }

      const nextIteration = currentIteration + 1;

      if(!error && nextIteration < numOfChanges) {
        setTimeout(async () => {
          await syncChange(changeSet[nextIteration], nextIteration);
        }, lastResponse && (!lastResponse.type || lastResponse.type === CodyResponseType.Info) ? CODY_APPLY_PAUSE : 0);
      } else {
        setTimeout(() => {
          finishSync();
        }, CODY_APPLY_PAUSE);
      }
    }

    const finishSync = () => {
      dispatch(togglePlayshotModal(props.match.params.uid, true));
      setPlayshotComparision({...playshotComparision, error, applied: true});
    }

    const handlePlayshotSync = () => {
      if(syncError) {
        return;
      }

      if(numOfChanges > 0) {
        dispatch(togglePlayshotModal(props.match.params.uid, false));
        dispatch(toggleCodyConsole(props.match.params.uid, true));

        iio.print({
          cody: `I'm going to apply the changes. Lean back and watch how the board is updated. I'll open the Playshot modal again when everything is done.`
        }, 'Cody');

        window.setTimeout(async () => {
          await syncChange(changeSet[0], 0);
        }, CODY_APPLY_PAUSE)
      }
    }

    const switchPlayshot = (playshotId: string, side: 'left' | 'right') => {
      const playshotSummary = playshots.filter(p => p.uid === playshotId).first(undefined)

      if(!playshotSummary) {
        setPlayshotComparision({
          left: side === 'left' ? new Playshot({}) : playshotComparision.left,
          right: side === 'right' ? new Playshot({}) : playshotComparision.right
        })
      } else {
        loadPlayshot(board.uid, playshotSummary.uid).then(p => {
          setPlayshotComparision({
            left: side === 'left' ? p : playshotComparision.left,
            right: side === 'right' ? p : playshotComparision.right
          })
        })
      }
    }

    const playshotOptions = playshots.sort((pA, pB) => pA.savedAt < pB.savedAt? 1 : -1 )
      .map(p => ({
      key: p.uid,
      text: p.name,
      value: p.uid
    })).toJS();

    playshotOptions.push({
      key: 'empty-playshot',
      text: '- empty -',
      value: ''
    });

    return <Modal open={visible} closeIcon={true} onClose={handleClose}>
      <Modal.Header><Button basic={true} className="noborder" icon="chevron left" onClick={() => setPlayshotComparision(undefined)} /> Compare Playshots (beta)</Modal.Header>
      <Modal.Content scrolling={true} style={{minHeight: "40vh"}}>
        {!playshotComparision!.applied && !syncError && <Message warning={true}>This is a new feature available for testing (beta version). Use at own risk.</Message>}
        {playshotComparision!.applied && !playshotComparision.error && <Message success={true} onDismiss={() => setPlayshotComparision(undefined)}>{props.t('insp.playshots.sync_success')}</Message>}
        {playshotComparision!.applied && playshotComparision.error && <Message error={true} onDismiss={handleClose}>{props.t('insp.playshots.sync_error')}</Message>}
        {!playshotComparision!.applied && syncError && <SyncErrorMessage error={syncError} />}
        <div className={'ui grid'}>
          <div className={'two column row'}>
            <div className={'column'}>
              <span>Left: </span><Dropdown
              placeholder={'- empty -'}
              options={playshotOptions}
              value={playshotComparision.left.uid}
              label={'Left Playshot'}
              onChange={(e, {value}) => switchPlayshot(value as string, 'left')}
            />
            </div>
            <div className={'column'}>
              <div className={'column'}>
                <span>Right: </span><Dropdown
                placeholder={'- empty -'}
                options={playshotOptions}
                value={playshotComparision.right.uid}
                label={'Right Playshot'}
                onChange={(e, {value}) => switchPlayshot(value as string, 'right')}
              />
              </div>
            </div>
          </div>
        </div>
        <List className="playshot changes">
          {changeSet.map((c, index) => <PlayshotChangedDiff change={c} playshot={playshotComparision!.right}
                                                            key={`playshot_changed_diff_${index}`} id={`playshot-changed-id-${index}`}  />)}
        </List>
      </Modal.Content>
      <Modal.Actions>
        <Button basic={true} className="noborder" icon="sync" disabled={playshotComparision!.applied || !!syncError} content={props.t('insp.playshots.sync_playshot')}
                 onClick={handlePlayshotSync}/>
      </Modal.Actions>
    </Modal>
  }

  let prevPlayshot: PlayshotSummary | null = null;

  return <Modal open={visible} closeIcon={true} onClose={handleClose}>
    <Modal.Header>Cody Playshots</Modal.Header>
    <Modal.Content scrolling={true}>
      <p>{props.t('insp.playshots.select_playshot')}</p>
      {playshots.count() === 0 && <Message info={true}>{props.t('insp.playshots.empty')}</Message>}
      <Table unstackable={true} basic="very">
        <Table.Body>
          {playshots.sort((pA, pB) => pA.savedAt < pB.savedAt? 1 : -1 )
            .reverse() // Iterate trough oldest to newest, to pass previous playshot to table row
            .map(playshot => {
              const nextPlayshot = <PlayshotTableRow
                playshot={playshot}
                previous={prevPlayshot}
                users={users} key={playshot.uid}
                loading={loadingPlayshotId === playshot.uid}
                onInitPlayshot={handleInitPlayshot}
                onComparePlayshots={c => setPlayshotComparision(c)}
                onDeletePlayshot={handleDeletePlayshot}
              />;

              prevPlayshot = playshot;

              return nextPlayshot;
            })
            .reverse() // Reverse back, to show newest to oldest
            .toArray()}
        </Table.Body>
      </Table>
    </Modal.Content>
    <Modal.Actions>
      <Button basic={true} className="noborder" icon="play circle" content={props.t('insp.playshots.start_from_scratch')} onClick={handleFromScratch} />
    </Modal.Actions>
  </Modal>
};

export default withRouter(withNamespaces()(PlayshotModal));

const SyncErrorMessage = (props: {error: string | {title: string, msg: string}}) => {
  const title = typeof props.error === "string" ? undefined : props.error.title;
  const msg = typeof props.error === "string" ? props.error : props.error.msg;

  return <Message error={true}>
    {title && <MessageHeader>{title}</MessageHeader>}
    <p>
      {msg}
    </p>
  </Message>
}
